import React, { useContext, useState } from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import { CaretDown, CaretUp, Gradient } from "phosphor-react";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import { UserContext } from "context/user";
import { MapActions, MapContext } from "context/map";
import { deleteField } from "api/Field";
import { fieldMapImage, parseFieldArea } from "utils";
import Confirm from "components/Confirm";
import InputField from "components/Input";
import Dropdown from "components/Dropdown";
import Button from "components/Button";
import UploadFile from "components/Modal/UploadFile";
import useClearPopUps from "hooks/useClearPopUps";
import CheckBox from "components/CheckBox";
import PremiumCue from "../PremiumCue";
import "./index.scss";

const FieldListMenu = ({ menuOpened, toggleMenu, lastSelectedSourceRef }) => {
  const location = useLocation();
  const clearPopUps = useClearPopUps();
  const { token, profile } = useContext(UserContext);
  const { state, dispatch } = useContext(MapContext);
  const [openUploadFile, setOpenUploadFile] = useState(false);
  const { map, farm, selectedFields, fields, draw } = state;
  const [searchField, setSearchedField] = useState("");
  const actions = [
    {
      id: "draw",
      label: "Draw Field",
      handleClick: () => {
        location.state = { draw: true };
        draw.deleteAll();
        dispatch({ type: MapActions.SET_MANAGE_MODE });
        dispatch({ type: MapActions.SET_MEASURE_MODE, value: "" });
        clearPopUps();
        localStorage.removeItem("isMeasuring");
      },
    },
    {
      id: "import",
      label: "Import Field",
      handleClick: () => {
        setOpenUploadFile(true);
      },
    },
  ];
  const fieldActions = [
    {
      id: "edit",
      label: "Edit Field",
      handleClick: (event) => {
        const fieldId = event.target.dataset.value;
        draw.deleteAll();
        dispatch({ type: MapActions.SET_MANAGE_MODE, value: fieldId });
        dispatch({ type: MapActions.SET_MEASURE_MODE, value: "" });
        clearPopUps();
        localStorage.removeItem("isMeasuring");
        event.stopPropagation();
      },
    },
    {
      id: "delete",
      label: "Delete Field",
      handleClick: (event) => {
        const fieldId = event.target.dataset.value;

        const removeField = async () => {
          try {
            await deleteField(fieldId, token);

            if (map.getLayer(`outline-${fieldId}`)) {
              map.removeLayer(`outline-${fieldId}`);
            }

            if (map.getLayer(`fill-${fieldId}`)) {
              map.removeLayer(`fill-${fieldId}`);
            }

            if (map.getSource(`source-${fieldId}`)) {
              map.removeSource(`source-${fieldId}`);
            }

            dispatch({
              type: MapActions.REMOVE_FIELD,
              value: fieldId,
            });
          } catch {
            toast.error("Can't delete field!");
          }
        };

        const confirmRemoveField = () => {
          const options = {
            title: "Are you sure?",
            message: "Deleting this field will removed all associated layers.",
            buttons: [
              {
                label: "Yes, delete anyway.",
                onClick: () => {
                  removeField();
                },
                className: "-danger",
              },
              {
                label: "No",
                onClick: () => {},
                className: "",
              },
            ],
            childrenElement: () => <div />,
            customUI: ({ title, message, buttons, onClose }) => (
              <Confirm
                title={title}
                message={message}
                buttons={buttons}
                onClose={onClose}
              />
            ),
            closeOnEscape: false,
            closeOnClickOutside: false,
            overlayClassName: "overlay-custom-class-name",
          };

          confirmAlert(options);
        };

        confirmRemoveField();

        event.stopPropagation();
      },
    },
  ];

  const handleSelectAllFields = () => {
    const currentSelectedFields = fields.filter((field) =>
      selectedFields.includes(field.id)
    );
    const multiSelectEnabled = profile.feature_flags.find(
      (item) =>
        item.feature_flag === "field:multi_select" && item.enabled === true
    );
    if (fields.length === selectedFields.length) {
      dispatch({
        type: MapActions.REMOVE_SELECTED_FIELDS,
      });
    } else if (
      currentSelectedFields.length > 0 &&
      fields.length !== selectedFields.length
    ) {
      dispatch({
        type: MapActions.REMOVE_SELECTED_FIELDS,
      });
      fields.map((field) =>
        dispatch({
          type: MapActions.SET_SELECTED_FIELDS,
          value: { fieldId: field.id, multiSelectEnabled },
        })
      );
    } else {
      fields.map((field) =>
        dispatch({
          type: MapActions.SET_SELECTED_FIELDS,
          value: { fieldId: field.id, multiSelectEnabled },
        })
      );
    }

    // eslint-disable-next-line no-param-reassign
    lastSelectedSourceRef.current = "fieldList";
  };

  const handleSelectField = (field) => {
    const multiSelectEnabled = profile.feature_flags.find(
      (item) =>
        item.feature_flag === "field:multi_select" && item.enabled === true
    );

    dispatch({
      type: MapActions.SET_SELECTED_FIELDS,
      value: { fieldId: field.id, multiSelectEnabled },
    });

    // eslint-disable-next-line no-param-reassign
    lastSelectedSourceRef.current = "fieldList";
  };

  const renderLabel = () => {
    let text = "Select field";

    if (selectedFields.length === 1) {
      const currentField = fields.filter((field) =>
        field.id.includes(selectedFields)
      );
      text = currentField[0].properties?.field_name;
    }

    if (selectedFields.length > 1) {
      text = `${selectedFields.length} fields`;
    }

    return (
      <div
        className={`menu-button ${
          menuOpened === "field-list" ? "-active" : ""
        }`}
        onClick={() =>
          menuOpened === "field-list"
            ? toggleMenu(null)
            : toggleMenu("field-list")
        }
        aria-hidden
      >
        <label className="label">Field:</label>
        <p className="count">{text}</p>
        {menuOpened === "field-list" ? (
          <CaretUp size={16} weight="fill" color="#B6B9C2" />
        ) : (
          <CaretDown size={16} weight="fill" color="#B6B9C2" />
        )}
      </div>
    );
  };

  const renderFields = () => {
    const searchedFields = fields.filter((field) =>
      field.properties.field_name
        .toLowerCase()
        .includes(searchField.toLowerCase())
    );

    if (searchedFields.length === 0) {
      return (
        <ul className="field-list">
          <div className="field-blank">
            <Gradient size={40} color="#8a8e99" />
            <p className="text">No fields found</p>
          </div>
        </ul>
      );
    }

    return (
      <>
        <div className="checkbox-header">
          <CheckBox
            onChange={() => handleSelectAllFields()}
            checked={fields.length === selectedFields.length}
          />
          <label className="label">Select All</label>
        </div>
        <ul className="field-list">
          {searchedFields.map((field, index, array) => (
            <li
              key={field.id}
              className={`field-item ${
                selectedFields.includes(field.id) ? "-selected" : ""
              }`}
            >
              <div className="field-checkbox">
                <CheckBox
                  onChange={() => handleSelectField(field)}
                  checked={!!selectedFields.includes(field.id)}
                />
              </div>
              <div
                className="details-wrapper"
                onClick={() => handleSelectField(field)}
                aria-hidden
              >
                <img
                  src={fieldMapImage(field)}
                  alt="field-map"
                  className="image"
                />
                <div className="info">
                  <h4 className="name">{field?.properties?.field_name}</h4>
                  <div className="crop-area">
                    <h5 className="crop">
                      {field?.properties?.field_crop_type === ""
                        ? "No Crop Type"
                        : field?.properties?.field_crop_type}
                    </h5>
                    <h5 className="area">
                      {parseFieldArea(field?.properties?.area)} Ha
                    </h5>
                  </div>
                </div>
                <Dropdown
                  iconOnly
                  icon="more"
                  data={fieldActions}
                  value={field.id}
                  className={
                    array.length > 1 && array.length - 1 <= index ? "-last" : ""
                  }
                />
              </div>
            </li>
          ))}
        </ul>
      </>
    );
  };

  const renderOptions = () => {
    return (
      <div className="fields-options">
        <Dropdown
          icon="add"
          buttonBlock
          buttonLabel="Add Field"
          buttonColor="green"
          buttonType="solid"
          data={actions}
        />
        <Button
          icon="edit"
          type="solid"
          color="green"
          label="Edit Fields"
          customClassName="-edit"
          onClick={() => {
            dispatch({ type: MapActions.SET_MANAGE_MODE });
            draw.deleteAll();
            dispatch({ type: MapActions.SET_MEASURE_MODE, value: "" });
            clearPopUps();
            localStorage.removeItem("isMeasuring");
          }}
        />
      </div>
    );
  };

  const renderListContainer = () => {
    return (
      <div
        className={`list-container ${
          menuOpened === "field-list" ? "" : "-hidden"
        }`}
      >
        <p className="container-title">Select Field</p>
        <PremiumCue text="Enable multi-field selection" />
        <InputField
          inputOnly
          append
          icon="search"
          placeholder="Search field..."
          onChange={(e) => setSearchedField(e.target.value)}
          value={searchField}
        />
        {renderFields()}
        {renderOptions()}
      </div>
    );
  };

  return (
    <>
      <div className="menu-container -field" data-tour="step-1">
        <div>
          {renderLabel()}
          {renderListContainer()}
        </div>
      </div>
      <UploadFile
        isOpen={openUploadFile}
        onRequestClose={() => {
          setOpenUploadFile(false);
        }}
        onClose={() => {
          setOpenUploadFile(false);
        }}
        farmName={farm}
        show="Add"
        handleChangeStatus={() => {}}
      />
    </>
  );
};

FieldListMenu.defaultProps = {
  menuOpened: null,
  lastSelectedSourceRef: { current: null },
};

FieldListMenu.propTypes = {
  menuOpened: PropTypes.string,
  toggleMenu: PropTypes.func.isRequired,
  lastSelectedSourceRef: PropTypes.shape({
    current: PropTypes.string,
  }),
};

export default FieldListMenu;
